import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses, OK, ERROR } from "helpers/data-statuses"
import { usePostQuery } from "./queries"
import { useMutation } from "@tanstack/react-query"
import { useCallback, useRef, useEffect } from "react"
import { useDispatch } from "react-redux"
import { Creators, createAbortController } from "model/apiWrapper"
import { isEmpty } from "lodash"

export const QUICK_ANSWERS_ERRORS = {
  AI_ERROR: "AI_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  NO_RESULTS: "NO_RESULTS",
  PORTFOLIO_ONLY: "PORTFOLIO_ONLY",
  BAD_QUESTION: "BAD_QUESTION",
}

export const ABORTED = "aborted"

export function useAIQuickAnswers({ question, modelId, resourceId, resourceType, base, enabled = false }) {
  const account = useAccountAlias()

  const shouldRunQuery = enabled && !!question && !!modelId && !!resourceId && !!resourceType && !!account

  const queryResults = usePostQuery({
    url: `/api/${account}/ai/quick-answers`,
    payload: {
      question,
      modelId,
      resourceId,
      resourceType: resourceType.toLowerCase() === "basket" ? "SWAP" : resourceType,
      base,
    },
    key: ["aiQuickAnswers", account, modelId, resourceId, resourceType, question],
    select: (queryResult) => {
      const { records, message } = queryResult
      return { records, message }
    },
    enabled: shouldRunQuery,
  })
  if (!shouldRunQuery) {
    return [null, OK]
  }

  const status = combineTanStackStatuses(queryResults)
  return [queryResults?.data, status]
}

// This hook lets us have an awaitable mutation function that works with our Redux Saga authentication
// We are implementing a custom Promise here because we will want to be able to abort the request if the user clicks a cancel button
export function useAIQuickAnswersMutation(endDate, startDate) {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  // Store current abort controller and request ID
  const currentAbortControllerRef = useRef(null)
  const currentRequestIdRef = useRef(null)

  // Custom mutation function that works with Redux Saga
  const mutationFn = useCallback(async ({ question, modelId, resourceId, resourceType, base }) => {
    if (!account || !question || !modelId || !resourceId || !resourceType) {
      throw new Error("Missing required parameters")
    }

    // Create abort controller for this request
    const { controller, requestId, signal } = createAbortController()
    currentAbortControllerRef.current = controller
    currentRequestIdRef.current = requestId

    const clearAbortHandler = () => {
      signal.onabort = null
      if (currentRequestIdRef.current === requestId) {
        currentAbortControllerRef.current = null
        currentRequestIdRef.current = null
      }
    }

    return new Promise((resolve, reject) => {
      // Set up abort handler using the signal's onabort property
      signal.onabort = () => {
        reject(new Error("Request was aborted"))
      }

      dispatch(Creators.post(
        `/api/${account}/ai/quick-answers`,
        {
          question,
          modelId,
          resourceId,
          resourceType: resourceType.toLowerCase() === "basket" ? "SWAP" : resourceType,
          base,
          startDate,
          endDate,
        },
        (result) => {
          clearAbortHandler()
          resolve(result)
        },
        (error) => {
          clearAbortHandler()
          reject(error)
        },
        signal,
        requestId,
      ))
    })
  }, [account, dispatch, endDate, startDate])

  const mutation = useMutation({
    mutationFn,
  })

  const askQuestion = useCallback(async (params) => {
    // Abort any existing request
    if (currentAbortControllerRef.current) {
      currentAbortControllerRef.current.abort()
    }

    try {
      const result = await mutation.mutateAsync(params)
      return handleSuccessfulResponse(result)
    } catch (error) {
      return handleErrorResponse(error)
    }
  }, [handleErrorResponse, handleSuccessfulResponse, mutation])

  const handleSuccessfulResponse = useCallback((result) => {
    const { records = [], ok, message, explain_steps, columns = [] } = result

    const isBadQuestion = !isEmpty(message) && ok === false

    if (isBadQuestion) {
      return [null, OK, QUICK_ANSWERS_ERRORS.BAD_QUESTION, message]
    }

    const hasContent = records.length > 0 || explain_steps || columns.length > 0

    if (isEmpty(message) && ok && hasContent) {
      return [result, OK, null]
    }

    if (isEmpty(message) && ok && !hasContent) {
      return [result, OK, QUICK_ANSWERS_ERRORS.NO_RESULTS]
    }

    // Other AI errors
    return [null, ERROR, QUICK_ANSWERS_ERRORS.AI_ERROR, null]
  }, [])

  const handleErrorResponse = useCallback((error) => {
    if (error.message === "Request was aborted" || error.name === "AbortError") {
      return [null, ABORTED, null]
    }

    // Because api/utils/processResponse() throws a TechnicalError, we have to use the error.error to determine the type of error
    if (error?.error === "AI processing failed") {
      return [null, ERROR, QUICK_ANSWERS_ERRORS.AI_ERROR, null]
    } else if (error.code === 500) {
      return [null, ERROR, QUICK_ANSWERS_ERRORS.SERVER_ERROR, null]
    } else {
      return [null, ERROR, QUICK_ANSWERS_ERRORS.AI_ERROR, null]
    }
  }, [])

  const abortCurrentRequest = useCallback(() => {
    if (currentAbortControllerRef.current) {
      currentAbortControllerRef.current.abort()
      currentAbortControllerRef.current = null
      currentRequestIdRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentAbortControllerRef.current) {
        currentAbortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    askQuestion,
    abortCurrentRequest,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
